import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import api from 'api';
import {
  GetEmployeeAdditionsDeductionsResponseContent,
  LopSourceToDeductionTypeMap,
} from 'api/gql-utils/salaryComponents/types';
import {
  AdditionsV2,
  DeductFrom,
  DeductFromLabelToNumberMap,
  DeductionComponentV2,
  DeductionType,
} from 'components/RunPayroll/utils/types';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { AppError } from 'utils/AppError';
import { paiseAmountToRupee, uniqueId } from 'utils/Numbers';
import queryKeys from '../components/queries';
import {
  EditPayrollProps,
  PayrollAllowanceV2,
  UpdateAdditionsDeductionsLopPayload,
} from '../types';
import { makeArrayOfMinimumLength } from '../utils';
import { ExistingSalaryComponents } from 'components/Fnf/types';
import { useToast } from '@razorpay/blade/components';

interface UseAdditionsDeductionsLopProps extends EditPayrollProps {
  onUpdateSuccess: () => void;
}

const defaultDeduction = {
  id: '',
  name: '',
  amount: 0,
  deduct_from: DeductFrom.GROSS_PAY,
  lop_days: '0',
  type: DeductionType.TYPE_MANUAL,
  label: '',
  server_id: '',
  additionalSdiData: undefined,
};

const useAdditionsDeductionsLop = ({
  payrollEntry,
  payrollMonth,
  onUpdateSuccess,
}: UseAdditionsDeductionsLopProps) => {
  const { isM2S1Enabled } = useIsFeatureDisabledForRearch();
  const toast = useToast();
  const [allowanceEntries, setAllowanceEntires] = useState<AdditionsV2[]>([]);
  const [existingComponents, setExistingComponents] = useState<ExistingSalaryComponents>({
    additions: [],
    deductions: [],
  });
  const [deductions, setDeductions] = useState<DeductionComponentV2>({
    lop_deduction: defaultDeduction,
    non_lop_deductions: Array.from({ length: 2 }, () => ({ ...defaultDeduction, id: uniqueId() })),
    total_deduction: payrollEntry.deductions?.total_deduction ?? 0,
  });
  const initialAllowances = (allowanceEntries: AdditionsV2[]) => {
    const initialItems = allowanceEntries.length <= 2 ? 2 : allowanceEntries.length;
    return Array.from({ length: initialItems }, (_, index) => ({
      ...allowanceEntries[index],
      id: allowanceEntries[index]?.id ?? uniqueId(),
      name: allowanceEntries[index]?.name ?? '',
      amount: allowanceEntries[index]?.amount ?? 0,
      payType: allowanceEntries[index]?.payType || '',
    }));
  };
  const [allowances, setAllowances] = useState<PayrollAllowanceV2[]>(
    initialAllowances(allowanceEntries),
  );

  const [deletedVariableAllowances, setDeletedVariableAllowances] = useState<PayrollAllowanceV2[]>(
    [],
  );

  const onSuccessHandler = (data: GetEmployeeAdditionsDeductionsResponseContent) => {
    // Extract the data from the additions response and format it into AdditionsV2 type
    let additions = data.additions.map((addition) => {
      return {
        id: addition.componentDetails.id,
        name: addition.componentDetails.name,
        amount: paiseAmountToRupee(addition.amount),
        type: addition.type,
        server_id: addition.id,
        additionalSdiData: addition.additionalSdiData,
        payType: addition?.componentDetails?.settings?.earningDetails?.payType || 'variable',
        componentId: addition.id,
      };
    });
    setAllowanceEntires(additions);
    setAllowances(initialAllowances(additions)); // Set the allowances

    const nonLOPDeductions = data.deductions.map((deduction) => ({
      id: deduction.componentDetails.id,
      name: deduction.componentDetails.name,
      amount: paiseAmountToRupee(deduction.amount),
      deduct_from:
        DeductFromLabelToNumberMap[
          deduction.componentDetails.settings.deductionDetails.deductFrom ?? 'GROSS_PAY'
        ],
      lop_days: '0',
      type: Number(deduction.type),
      label: deduction.componentDetails.name,
      server_id: deduction.id,
      additionalSdiData:
        Object.keys(deduction?.additionalSdiData ?? {}).length > 0
          ? deduction.additionalSdiData
          : deduction.componentDetails.settings.deductionDetails.deductionType === 'RECURRING'
          ? {
              deduction_type: 'recurring' as const,
              adhoc_payment_id: '',
            }
          : undefined,
    }));

    setExistingComponents({
      additions,
      deductions: nonLOPDeductions,
    });

    // Extract the data from the deductions and LOP Days response and format it into DeductionsV2 type
    setDeductions((prev) => {
      const firstLopData = data.lopData?.[0];
      const ded = {
        ...prev,
        non_lop_deductions: makeArrayOfMinimumLength(nonLOPDeductions, 2, defaultDeduction),
        lop_deduction: {
          ...prev.lop_deduction,
          type: firstLopData?.source
            ? LopSourceToDeductionTypeMap[firstLopData.source]
            : prev.lop_deduction.type,
          lop_days: firstLopData?.lopDays
            ? String(firstLopData.lopDays)
            : prev.lop_deduction.lop_days,
        },
      };
      return ded;
    });
  };

  const { error, isFetching } = useQuery({
    queryKey: [queryKeys.QUERY_LIST_ADDITIONS_DEDUCTIONS_LOP, payrollMonth, payrollEntry.peopleId],
    queryFn: () =>
      api.salaryComponents.queries.getEmployeeAdditionsDeductions({
        userId: String(payrollEntry.peopleId),
        effectiveFrom: payrollMonth,
        effectiveTo: payrollMonth,
        payType: 'LIST_ADHOC_TYPE',
      }),
    onSuccess: (response) => {
      onSuccessHandler(response.payrollGetEmployeeAdditionsDeductions.data);
    },
    enabled: isM2S1Enabled,
    onError: (error: AppError) => {
      toast.show({
        color: 'negative',
        content: 'Something went wrong, please try again later',
      });
    },
  });

  const updateAdditionsDeductionsLop = useMutation(
    (payload: UpdateAdditionsDeductionsLopPayload) => {
      return api.salaryComponents.mutations.updateEmployeesAdditionsDeductionsLop(payload);
    },
    {
      onSuccess: (response) => {
        onSuccessHandler(response.payrollUpdateEmployeeAdditionsDeductions.data);
        onUpdateSuccess();
      },
      onError: (error: AppError) => {},
    },
  );

  const onAllowanceChange = (updateId: string, updates: Partial<PayrollAllowanceV2>) => {
    setAllowances(
      allowances.map((val, i) => {
        if (val.id === updateId) {
          return {
            ...val,
            ...updates,
          };
        }
        return val;
      }),
    );
  };

  const onAllowanceAdd = () => {
    setAllowances((prev) => [
      ...prev,
      {
        id: uniqueId(),
        amount: 0,
        name: '',
      },
    ]);
  };

  const onAllowanceDelete = (deleteItem: PayrollAllowanceV2) => {
    if (deleteItem.payType === 'variable') {
      setDeletedVariableAllowances((prev) => [...prev, deleteItem]);
    }
    setAllowances((prev) => prev.filter((val) => val.id !== deleteItem.id));
  };

  const deleteVariableAdditions = useMutation(
    (payload: PayrollAllowanceV2[]) => {
      return api.salaryComponents.queries.deleteVariableAdditions([
        {
          userId: String(payrollEntry.peopleId),
          primaryIds: deletedVariableAllowances.map((item) => item?.componentId || ''),
          payrollMonth: '',
        },
      ]);
    },
    {
      onSuccess: (response) => {},
      onError: (error: AppError) => {
        toast.show({
          color: 'negative',
          content: 'Something went wrong, please try again later',
        });
      },
    },
  );

  return {
    allowances,
    onAllowanceChange,
    onAllowanceAdd,
    onAllowanceDelete,
    deductions,
    setDeductions,
    updateAdditionsDeductionsLop,
    deletedVariableAllowances,
    isAdditionsDeductionsListLoading: isFetching,
    errorMessage: (error || updateAdditionsDeductionsLop.error)?.message,
    existingComponents,
    deleteVariableAdditions,
  };
};

export default useAdditionsDeductionsLop;
